//----------------------------------------------------------------------------------------------------------------------
// Asset Manager
//----------------------------------------------------------------------------------------------------------------------

import type { AbstractMesh, AssetContainer, InstancedMesh, Mesh } from '@babylonjs/core';

import type { GameEventBus } from '../classes/eventBus.ts';
import type { SceneEngine } from '../engines/scene.ts';
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';
import { type LoggingUtility, SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

interface CachedContainer
{
    container : AssetContainer;
    refCount : number;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Central asset cache with preloading, GLB fragment extraction, mesh instancing, and reference counting.
 *
 * Supports fragment syntax (`models/props.glb#chest_lid`) to extract individual meshes from a shared container.
 * Containers are reference-counted and only disposed when all consumers release them.
 */
export class AssetManager implements Disposable
{
    private _eventBus : GameEventBus;
    private _sceneEngine : SceneEngine;
    private _log : LoggerInterface;

    /** Full GLB/GLTF asset containers, keyed by base path (without fragment) */
    private _containers = new Map<string, CachedContainer>();

    /** Extracted fragment meshes (source meshes for instancing), keyed by full fragment path */
    private _sourceMeshes = new Map<string, AbstractMesh>();

    //------------------------------------------------------------------------------------------------------------------

    constructor(eventBus : GameEventBus, sceneEngine : SceneEngine, logger ?: LoggingUtility)
    {
        this._eventBus = eventBus;
        this._sceneEngine = sceneEngine;
        this._log = logger?.getLogger('AssetManager') ?? new SAGELogger('AssetManager');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Helpers
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Split a path on `#` into base path and optional fragment name.
     */
    private _parsePath(path : string) : { basePath : string; fragment ?: string }
    {
        const hashIdx = path.indexOf('#');
        if(hashIdx === -1)
        {
            return { basePath: path };
        }

        return {
            basePath: path.substring(0, hashIdx),
            fragment: path.substring(hashIdx + 1),
        };
    }

    /**
     * Load (or retrieve from cache) the AssetContainer for a base path.
     * Increments refCount.
     */
    private async _loadContainer(basePath : string) : Promise<AssetContainer>
    {
        const cached = this._containers.get(basePath);
        if(cached)
        {
            cached.refCount++;
            return cached.container;
        }

        this._log.debug(`Loading asset container: ${ basePath }`);

        // LoadAssetContainerAsync needs a scene; create a temporary one
        const tempScene = this._sceneEngine.createScene();

        try
        {
            const container = await this._sceneEngine.loadModel(basePath, tempScene);

            this._containers.set(basePath, { container, refCount: 1 });
            this._log.debug(`Cached asset container: ${ basePath }`);

            return container;
        }
        finally
        {
            tempScene.dispose();
        }
    }

    /**
     * Find a mesh by name inside a container. Throws if not found.
     */
    private _findMeshInContainer(container : AssetContainer, meshName : string, fullPath : string) : AbstractMesh
    {
        const mesh = container.meshes.find((entry) => entry.name === meshName);
        if(!mesh)
        {
            const available = container.meshes.map((entry) => entry.name).join(', ');
            throw new Error(
                `Mesh '${ meshName }' not found in container for '${ fullPath }'. `
                + `Available meshes: ${ available }`
            );
        }

        return mesh;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Load an asset, caching the result. Supports fragment syntax (`path#meshName`).
     *
     * - Without fragment: loads and caches the full AssetContainer, returns it.
     * - With fragment: loads the container, finds the named mesh, caches it as a source mesh, returns the mesh.
     *
     * Increments refCount on the underlying container each time.
     */
    async load(path : string) : Promise<AssetContainer | AbstractMesh>
    {
        const { basePath, fragment } = this._parsePath(path);

        if(fragment)
        {
            // Check if we already cached this fragment mesh
            const cachedMesh = this._sourceMeshes.get(path);
            if(cachedMesh)
            {
                // Still increment refCount on the container
                const cached = this._containers.get(basePath);
                if(cached)
                {
                    cached.refCount++;
                }

                return cachedMesh;
            }

            // Load the container (handles caching + refCount internally)
            const container = await this._loadContainer(basePath);

            // Find and cache the named mesh
            const mesh = this._findMeshInContainer(container, fragment, path);
            this._sourceMeshes.set(path, mesh);

            return mesh;
        }

        // No fragment — return the full container
        return this._loadContainer(basePath);
    }

    /**
     * Create an InstancedMesh from a cached source mesh. The path must have been loaded first.
     */
    instance(path : string) : InstancedMesh
    {
        const sourceMesh = this._sourceMeshes.get(path);
        if(!sourceMesh)
        {
            throw new Error(`Asset '${ path }' is not loaded. Call load() first.`);
        }

        return (sourceMesh as Mesh).createInstance(`${ sourceMesh.name }-instance`);
    }

    /**
     * Clone a cached source mesh into an independent copy. The path must have been loaded first.
     */
    clone(path : string) : Mesh
    {
        const sourceMesh = this._sourceMeshes.get(path);
        if(!sourceMesh)
        {
            throw new Error(`Asset '${ path }' is not loaded. Call load() first.`);
        }

        return (sourceMesh as Mesh).clone(`${ sourceMesh.name }-clone`);
    }

    /**
     * Preload a batch of asset paths. Emits `asset:progress` after each and `asset:complete` when finished.
     */
    async preload(paths : string[]) : Promise<void>
    {
        const total = paths.length;

        for(let i = 0; i < paths.length; i++)
        {
            // eslint-disable-next-line no-await-in-loop
            await this.load(paths[i]);

            this._eventBus.publish({
                type: 'asset:progress',
                payload: {
                    path: paths[i],
                    loaded: i + 1,
                    total,
                },
            });
        }

        this._eventBus.publish({
            type: 'asset:complete',
            payload: { paths },
        });
    }

    /**
     * Decrement refCount for the container backing this path.
     * When refCount hits 0, dispose the container and clean up all fragment meshes from it.
     */
    dispose(path : string) : void
    {
        const { basePath } = this._parsePath(path);
        const cached = this._containers.get(basePath);
        if(!cached)
        {
            return;
        }

        cached.refCount--;

        if(cached.refCount <= 0)
        {
            this._log.debug(`Disposing asset container: ${ basePath } (refCount reached 0)`);

            // Clean up all fragment meshes that came from this container
            for(const [ fragmentPath, mesh ] of this._sourceMeshes)
            {
                if(fragmentPath.startsWith(`${ basePath }#`))
                {
                    mesh.dispose();
                    this._sourceMeshes.delete(fragmentPath);
                }
            }

            cached.container.dispose();
            this._containers.delete(basePath);
        }
    }

    /**
     * Dispose all cached containers and fragment meshes.
     */
    disposeAll() : void
    {
        for(const cached of this._containers.values())
        {
            cached.container.dispose();
        }

        for(const mesh of this._sourceMeshes.values())
        {
            mesh.dispose();
        }

        this._containers.clear();
        this._sourceMeshes.clear();
    }

    /**
     * Lifecycle teardown — disposes all assets.
     */
    async $teardown() : Promise<void>
    {
        this.disposeAll();
    }
}

//----------------------------------------------------------------------------------------------------------------------
